import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  IndexTable,
  InlineStack,
  Layout,
  Page,
  Select,
  Spinner,
  Text,
  TextField,
  Tooltip,
} from '@shopify/polaris';
import { RefreshIcon, SearchIcon } from '@shopify/polaris-icons';

import apiClient from '../utils/api';
import { emptyRow, headingsFor, itemCountFor } from '../utils/table';
import {
  CLASSIFICATIONS,
  CLASSIFICATION_OPTIONS,
  UNCLASSIFIED_COST,
  classificationBadge,
  describeDiscovery,
  isUndecided,
  readReasonRows,
  sortReasonRows,
  undecidedCount,
} from '../utils/refund-reasons';

/**
 * What each of a retailer's refund reason codes means, decided once, by a person.
 *
 * Every refund a retailer issues carries a reason code of the RETAILER's own invention, and
 * the same string means different things on different accounts - "14" is "No response from
 * the shop" on one and "Customer Support Appeasement" on another. Nothing can work the
 * meaning out: it has to be told, per retailer, and this is where it is told.
 *
 * EVERY SETTLED REFUND IS WRITTEN AND BILLED, whatever its code; a code decides only the
 * return fee (utils/refund-reasons.js). A code with no setting follows the quantity, so those
 * rows are listed first and counted in a banner as the ones somebody may want to check - a
 * lost parcel is the case where the quantity gets the fee wrong - not as money standing still.
 *
 * DECIDING IS AN EXPLICIT SAVE, never a write on blur or on change. The answer chosen here
 * decides whether a brand is charged a return fee; a control that commits as the cursor
 * leaves it turns a mis-click into a charge, and the person who made it never sees the
 * moment it happened. Nothing leaves this screen until Save is pressed, and a save that
 * fails leaves the stored answer on screen exactly as it is in the database.
 */

/**
 * No rows-per-page control, deliberately.
 *
 * A retailer has as many reason codes as it has invented, which is tens at the outside, and
 * the list route answers with all of them in one response - there is no `page` to send. A
 * pager over a complete list would also hide undecided rows behind a Next button, which is
 * the one thing this screen exists to prevent.
 */
/**
 * What the dropdown shows for a stored answer. A row still set to NEITHER, which is no longer
 * offered, is shown as the price adjustment it is written as, so saving it stores that.
 */
function selectableClassification(row) {
  if (row.classification === CLASSIFICATIONS.NEITHER) return CLASSIFICATIONS.PRICE_ADJUSTMENT;
  return row.classification || CLASSIFICATIONS.UNCLASSIFIED;
}

const COLUMNS = [
  {
    key: 'code',
    title: 'Reason code',
    tip: 'The retailer’s own code, stored exactly as it arrives on the refund. It is an '
      + 'opaque string and belongs to that retailer alone, so the same code on another '
      + 'retailer is a different thing and is decided separately.',
  },
  {
    key: 'label',
    title: 'Retailer’s wording',
    tip: 'What the retailer calls this code, typed in by whoever looked it up. It is a note '
      + 'for the next person and changes nothing about billing. Empty means nobody has '
      + 'written the wording down, which is not the same as the code being undecided.',
  },
  {
    key: 'decided',
    title: 'Decided as',
    tip: 'What is stored right now, and therefore how refunds on this code are billed today. '
      + 'It changes only when a save succeeds.',
  },
  {
    key: 'change',
    title: 'Change to',
    tip: 'The answer being proposed. Nothing is written until Save is pressed, so a wrong '
      + 'pick here costs nothing until then.',
  },
  {
    key: 'updated',
    title: 'Updated',
    tip: 'When this row was last written. A row that has never been decided shows when the '
      + 'scan first recorded the code.',
  },
  {
    key: 'actions',
    title: 'Save',
    tip: 'Writes the proposed answer for this one code. Each row is saved on its own, so one '
      + 'failure never leaves a screenful of half-written decisions.',
  },
];

const when = (value) => (value ? new Date(value).toLocaleString(undefined, {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '—');

/** The wording as stored, as a string the TextField can hold. Null means nobody wrote one. */
const storedLabel = (row) => (row?.label == null ? '' : String(row.label));

/**
 * What goes on the wire for the wording.
 *
 * An emptied box is sent as null rather than as an empty string, because null is what the
 * column means by "nobody has written the wording down" - storing '' would make a cleared
 * note indistinguishable from a note that was never taken, in a field whose whole purpose is
 * telling the next person what the retailer calls this code.
 */
const labelForWire = (draftLabel) => {
  const trimmed = draftLabel.trim();
  return trimmed === '' ? null : trimmed;
};

export default function RefundReasons() {
  // The retailer accounts to choose between. Read here rather than passed in, because this
  // screen is reached from the sidebar and has no parent holding them.
  const [marketplaces, setMarketplaces] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState(null);
  const [marketplaceId, setMarketplaceId] = useState('');

  // The stored rows, exactly as the API last reported them. Drafts are kept apart from these
  // on purpose: this state is the answer to "what does the database say", and a failed save
  // must leave it untouched.
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(null);

  // Per row, what somebody has typed or picked but not yet saved. Keyed by row id and absent
  // until the row is touched, so an untouched row always renders from the stored value.
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [rowErrors, setRowErrors] = useState({});
  const [savedNotice, setSavedNotice] = useState(null);

  const [scanning, setScanning] = useState(false);
  const [scanNotice, setScanNotice] = useState(null);
  const [scanError, setScanError] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await apiClient.getConnectorMarketplaces();
        if (!live) return;
        const list = Array.isArray(res) ? res : res?.marketplaces ?? [];
        setMarketplaces(list);
        setAccountsError(null);
      } catch (err) {
        if (!live) return;
        setAccountsError(err.message || 'Could not load the retailer accounts');
      } finally {
        if (live) setAccountsLoading(false);
      }
    })();
    return () => { live = false; };
  }, []);

  // One retailer at a time, defaulting to the first. There is no "all retailers" option and
  // there should not be: a code means nothing without the account it came from, so a merged
  // list would put two unrelated meanings of "14" side by side with nothing to tell them
  // apart - and the decision made on that screen would be made against the wrong retailer.
  useEffect(() => {
    if (!marketplaceId && marketplaces.length > 0) setMarketplaceId(marketplaces[0].id);
  }, [marketplaces, marketplaceId]);

  /**
   * Only the newest list request may write state - the same guard, for the same reason, as
   * the connector tables. Switching retailer twice quickly otherwise lets the slower first
   * response land last and paint one account's codes under the other account's name, which
   * on this screen is a decision made against the wrong retailer.
   */
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    if (!marketplaceId) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const res = await apiClient.getRetailerRefundReasons(marketplaceId);
      if (seq !== requestSeq.current) return;
      setRows(readReasonRows(res));
      setLoaded(true);
      setListError(null);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      // The rows are dropped on a failure rather than left on screen: they would be the
      // PREVIOUS retailer's codes under this retailer's name, and the error banner sits
      // above the table rather than over it.
      setRows([]);
      setLoaded(false);
      setListError(err.message || 'Could not load the refund reason codes for this retailer');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [marketplaceId]);

  useEffect(() => { load(); }, [load]);

  // Everything typed, every row error and every notice belongs to the retailer it was made
  // against. Carried across a switch, an unsaved answer would be applied to a code on an
  // account nobody chose it for, and a success message would vouch for rows it never saw.
  useEffect(() => {
    setDrafts({});
    setRowErrors({});
    setSavedNotice(null);
    setScanNotice(null);
    setScanError(null);
  }, [marketplaceId]);

  const ordered = useMemo(() => sortReasonRows(rows), [rows]);
  const undecided = useMemo(() => undecidedCount(rows), [rows]);
  const headings = useMemo(() => headingsFor(COLUMNS), []);

  const retailerName = useMemo(
    () => marketplaces.find((m) => m.id === marketplaceId)?.name || 'this retailer',
    [marketplaces, marketplaceId],
  );

  /** What the row shows in its editable controls: what was typed, or what is stored. */
  const draftFor = useCallback((row) => drafts[row.id] ?? {
    classification: selectableClassification(row),
    label: storedLabel(row),
  }, [drafts]);

  const setDraft = useCallback((row, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [row.id]: {
        ...(prev[row.id] ?? {
          classification: selectableClassification(row),
          label: storedLabel(row),
        }),
        ...patch,
      },
    }));
  }, []);

  /**
   * Whether this row has anything worth saving.
   *
   * Compared against the STORED values rather than against "has it been touched", so picking
   * an answer and picking the original one back leaves the Save button off: a save that
   * writes what is already there still stamps an updated timestamp and still reads, to the
   * next person, as a decision somebody made today.
   */
  const isDirty = useCallback((row) => {
    const draft = draftFor(row);
    const classificationChanged = draft.classification
      !== (row.classification || CLASSIFICATIONS.UNCLASSIFIED);
    const labelChanged = draft.label.trim() !== storedLabel(row).trim();
    return classificationChanged || labelChanged;
  }, [draftFor]);

  const discardDraft = useCallback((row) => {
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
  }, []);

  /**
   * Write one row's decision.
   *
   * On success the stored row is replaced with what the server answered, falling back to the
   * draft only for the fields it did not return - so the "Decided as" badge always shows what
   * is in the database rather than what was asked for. The list is then re-sorted by the
   * memo above, which is what moves a freshly decided code out of the undecided block.
   *
   * On failure NOTHING in `rows` is touched. The badge keeps saying what is still stored, the
   * draft stays exactly as it was typed so the save can be pressed again, and the row says
   * what went wrong. Optimistically painting the new answer here would show a decision that
   * does not exist, on the one screen whose rows decide whether a brand is charged.
   */
  const save = useCallback(async (row) => {
    const draft = draftFor(row);
    setSavingId(row.id);
    setSavedNotice(null);
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });

    try {
      const res = await apiClient.updateRefundReason(row.id, {
        classification: draft.classification,
        label: labelForWire(draft.label),
      });
      const saved = (res && typeof res === 'object' && (res.refundReason || res.reason || res))
        || {};

      setRows((prev) => prev.map((r) => (r.id === row.id
        ? {
          ...r,
          classification: saved.classification || draft.classification,
          label: saved.label !== undefined ? saved.label : labelForWire(draft.label),
          updatedAt: saved.updatedAt || new Date().toISOString(),
        }
        : r)));
      discardDraft(row);
      setSavedNotice(
        `${row.reasonCode} on ${retailerName} is now ${classificationBadge(saved.classification || draft.classification).label}.`,
      );
    } catch (err) {
      // The sentence names what is STILL stored, because the badge beside it has not moved
      // and somebody reading only the red text has to know the old answer is still in force.
      const unchanged = isUndecided(row)
        ? 'this code still has no setting, so refunds on it follow the quantity'
        : `this code still reads ${classificationBadge(row.classification).label}`;
      setRowErrors((prev) => ({
        ...prev,
        [row.id]: `${err.message || 'The save failed'} — nothing was written, ${unchanged}.`,
      }));
    } finally {
      setSavingId(null);
    }
  }, [draftFor, discardDraft, retailerName]);

  /**
   * Look for codes nobody has recorded.
   *
   * Reads orders already held for this retailer, so it can be pressed as often as anyone
   * likes: nothing is fetched from the retailer and a code somebody has already decided is
   * never touched. Whatever it adds arrives with no setting and follows the quantity.
   */
  const scan = useCallback(async () => {
    if (!marketplaceId) return;
    setScanning(true);
    setScanError(null);
    setScanNotice(null);
    setSavedNotice(null);
    try {
      const res = await apiClient.discoverRetailerRefundReasons(marketplaceId);
      setScanNotice(describeDiscovery(res));
      // Re-read rather than append what the scan reported: the response says what it did, the
      // list says what is stored, and the second is what the table has to show.
      await load();
    } catch (err) {
      setScanError(err.message || 'Could not scan the stored orders for this retailer');
    } finally {
      setScanning(false);
    }
  }, [marketplaceId, load]);

  const rowMarkup = ordered.map((row, index) => {
    const draft = draftFor(row);
    const stored = classificationBadge(row.classification);
    const undecidedRow = isUndecided(row);
    const dirty = isDirty(row);
    const rowError = rowErrors[row.id];

    return (
      <IndexTable.Row
        id={row.id}
        key={row.id}
        position={index}
      >
        <IndexTable.Cell>
          <Text variant="bodySm" fontWeight="semibold">{row.reasonCode}</Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Box minWidth="180px">
            <TextField
              label={`Retailer’s wording for ${row.reasonCode}`}
              labelHidden
              value={draft.label}
              onChange={(value) => setDraft(row, { label: value })}
              placeholder="What the retailer calls it"
              autoComplete="off"
              disabled={savingId === row.id}
            />
          </Box>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <BlockStack gap="050">
            <Box><Badge tone={stored.tone}>{stored.label}</Badge></Box>
            {/* The cost, on the row itself. The banner above counts them; this says what this
                particular code is doing to the money while it sits here. */}
            {undecidedRow && (
              <Text variant="bodySm" tone="subdued">Return if items came back, price adjustment if not.</Text>
            )}
          </BlockStack>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Box minWidth="260px">
            <Select
              label={`What ${row.reasonCode} means`}
              labelHidden
              options={CLASSIFICATION_OPTIONS}
              value={draft.classification}
              onChange={(value) => setDraft(row, { classification: value })}
              disabled={savingId === row.id}
            />
          </Box>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text variant="bodySm" tone="subdued">{when(row.updatedAt || row.createdAt)}</Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <BlockStack gap="100">
            <InlineStack gap="200" blockAlign="center">
              {/* Off until something has actually changed, so the button says whether there
                  is a decision outstanding on this row rather than being a permanent
                  invitation to rewrite what is already there. */}
              <Button
                onClick={() => save(row)}
                loading={savingId === row.id}
                disabled={!dirty || savingId !== null}
                variant={undecidedRow && dirty ? 'primary' : 'secondary'}
              >
                Save
              </Button>
              {dirty && savingId !== row.id && (
                <Button variant="plain" onClick={() => discardDraft(row)}>Discard</Button>
              )}
            </InlineStack>
            {rowError && (
              <Text variant="bodySm" tone="critical">{rowError}</Text>
            )}
          </BlockStack>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <Page
      title="Refund Reasons"
      subtitle="What each retailer’s refund codes mean, and therefore which refunds carry a return fee"
      /* The scan is NOT the page's primary action, which is where this console usually puts
         an action of this kind. It acts on one retailer, so it belongs beside the retailer
         selector that says which one — a button in the page header above a dropdown it
         silently depends on invites a scan of the account somebody has just switched away
         from. It also carries the sentence explaining that it only reads orders already held
         and never changes a decision, and a page action has nowhere to put that. */
      secondaryActions={[
        {
          content: 'Refresh',
          icon: RefreshIcon,
          onAction: load,
          disabled: !marketplaceId,
        },
      ]}
      fullWidth
    >
      <Layout>
        {accountsError && (
          <Layout.Section>
            <Banner tone="critical">{accountsError}</Banner>
          </Layout.Section>
        )}

        {listError && (
          <Layout.Section>
            <Banner
              tone="critical"
              action={{ content: 'Try again', onAction: load }}
            >
              {listError}
            </Banner>
          </Layout.Section>
        )}

        {scanError && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setScanError(null)}>{scanError}</Banner>
          </Layout.Section>
        )}

        {scanNotice && (
          <Layout.Section>
            <Banner tone={scanNotice.tone} onDismiss={() => setScanNotice(null)}>
              {scanNotice.message}
            </Banner>
          </Layout.Section>
        )}

        {savedNotice && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSavedNotice(null)}>{savedNotice}</Banner>
          </Layout.Section>
        )}

        {/* How many codes follow the quantity, above the table: the ones worth a look, because
            a code like a lost parcel can carry a quantity that gets the fee wrong. */}
        {loaded && undecided > 0 && (
          <Layout.Section>
            <Banner
              tone="info"
              title={`${undecided} refund reason code${undecided === 1 ? '' : 's'} on ${retailerName} ${undecided === 1 ? 'has' : 'have'} no setting`}
            >
              <Text as="p">
                {`${UNCLASSIFIED_COST} They are listed first below. Set one only where the quantity gets the fee wrong, such as a lost parcel.`}
              </Text>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <InlineStack gap="300" blockAlign="center" wrap>
                  {accountsLoading && <Spinner size="small" accessibilityLabel="Loading retailers" />}
                  {!accountsLoading && marketplaces.length > 0 && (
                    <Box minWidth="240px">
                      <Select
                        label="Retailer"
                        options={marketplaces.map((m) => ({ label: m.name, value: m.id }))}
                        value={marketplaceId}
                        onChange={setMarketplaceId}
                      />
                    </Box>
                  )}
                  <Tooltip content="Reads the orders already held for this retailer and records every refund reason code they carry that is not on this list. Nothing is fetched from the retailer, and a code somebody has already decided is never changed. Anything new arrives with no setting and follows the quantity.">
                    <Button
                      icon={SearchIcon}
                      onClick={scan}
                      loading={scanning}
                      disabled={!marketplaceId}
                    >
                      Scan stored orders for new codes
                    </Button>
                  </Tooltip>
                </InlineStack>

                {!accountsLoading && marketplaces.length === 0 && !accountsError && (
                  <Text tone="subdued" variant="bodySm">
                    No retailer accounts yet. Add one on Retailer Accounts and fetch its orders,
                    then scan here for the refund codes those orders carry.
                  </Text>
                )}

                {marketplaces.length > 0 && (
                  /* No heading is sortable, so no sortToggleLabels are needed and every `tip`
                     renders: Polaris silently discards a sortable heading's tooltipContent
                     unless that prop is supplied and complete. The order is this screen's own
                     - undecided first - and is not a column anybody may re-sort away. */
                  <IndexTable
                    resourceName={{ singular: 'reason code', plural: 'reason codes' }}
                    // 1 when there is nothing, so the header row survives: at itemCount 0
                    // IndexTable swaps the WHOLE table for emptyState, column names included.
                    itemCount={itemCountFor(ordered)}
                    selectable={false}
                    headings={headings}
                    loading={loading}
                  >
                    {ordered.length === 0
                      ? emptyRow(
                        COLUMNS,
                        'refund-reasons',
                        listError
                          ? 'The reason codes for this retailer could not be read. Nothing here '
                            + 'is lost — press Try again above.'
                          : 'No refund reason codes recorded for this retailer yet. Press Scan '
                            + 'stored orders for new codes: it reads the orders already held '
                            + 'for this retailer and lists every code they carry, so there is '
                            + 'something to decide. Until a code is recorded and decided, '
                            + 'refunds on it are not billed and earn no return fee.',
                      )
                      : rowMarkup}
                  </IndexTable>
                )}
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
